import type { LineString } from 'geojson';

import { config } from '../config';

export type RouteProfile = 'truck' | 'auto';

/** См. BorderPolicy в gar/src/services/RoutingService.ts */
export type BorderPolicy = 'ALLOW' | 'AVOID' | 'FORBID';

export interface RoutePoint {
  readonly lat: number;
  readonly lon: number;
}

export interface TruckParams {
  /** м */
  readonly height?: number;
  /** т */
  readonly weight?: number;
  /** т */
  readonly axleLoad?: number;
}

export interface RouteRequest {
  readonly points: readonly RoutePoint[];
  readonly profile: RouteProfile;
  readonly truck?: TruckParams;
  readonly borders: BorderPolicy;
  readonly avoid: { readonly tolls: boolean; readonly ferries: boolean };
  /** Сколько альтернативных маршрутов запросить (0-2); бэкенд строит их
   *  только между двумя точками. */
  readonly alternatives?: number;
}

export interface RouteManeuver {
  readonly instruction: string;
  readonly type: number;
  readonly streetNames: readonly string[];
  readonly distanceKm: number;
  readonly durationSec: number;
}

interface RouteResponse {
  readonly distanceKm: number;
  readonly durationSec: number;
  readonly hasToll: boolean;
  readonly hasFerry: boolean;
  readonly countries: readonly { code: string; name: string; distanceKm: number }[];
  readonly regions: readonly { countryCode: string; code: string; name: string; distanceKm: number }[];
  /** Линия маршрута в Google Encoded Polyline (запрашиваем geometryFormat=POLYLINE). */
  readonly polyline: string;
  /** Километров по платным дорогам. */
  readonly tollDistanceKm?: number;
  /** Платные отрезки, тоже polyline. */
  readonly tollSections?: readonly { distanceKm: number; polyline: string }[];
  /** [minLon, minLat, maxLon, maxLat] */
  readonly bbox: readonly [number, number, number, number];
  readonly legs: readonly { distanceKm: number; durationSec: number; maneuvers: readonly RouteManeuver[] }[];
  readonly alternatives?: readonly RouteResponse[];
}

export interface RouteTollSection {
  readonly distanceKm: number;
  readonly geometry: LineString;
}

export interface Route extends Omit<RouteResponse, 'polyline' | 'tollDistanceKm' | 'tollSections' | 'alternatives'> {
  /** Декодированная polyline - то, что рисует карта. */
  readonly geometry: LineString;
  readonly tollDistanceKm: number;
  /** Платные отрезки - подсвечиваются на карте другим цветом. */
  readonly tollSections: readonly RouteTollSection[];
  /** Альтернативные маршруты (без своих альтернатив); пусто, если их нет. */
  readonly alternatives: readonly Route[];
}

/**
 * Google Encoded Polyline -> GeoJSON LineString. Точность 1e-5, точки в
 * строке идут как lat,lon, в GeoJSON - [lon, lat].
 */
export function decodePolyline(encoded: string): LineString {
  const coordinates: [number, number][] = [];
  let index = 0;
  let lat = 0;
  let lon = 0;

  const nextValue = () => {
    let result = 0;
    let shift = 0;
    let byte: number;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    return result & 1 ? ~(result >> 1) : result >> 1;
  };

  while (index < encoded.length) {
    lat += nextValue();
    lon += nextValue();
    coordinates.push([lon / 1e5, lat / 1e5]);
  }

  return { type: 'LineString', coordinates };
}

/**
 * Ошибка с текстом от бэкенда ("Маршрут не найден...", "Одна из точек
 * слишком далеко от дорог") - его можно показывать пользователю как есть.
 */
export class RouteError extends Error {}

export async function fetchRoute(request: RouteRequest, signal?: AbortSignal): Promise<Route> {
  const response = await fetch(new URL('/route', config.apiBaseUrl).toString(), {
    method: 'POST',
    signal,
    headers: { 'X-Api-Key': config.apiKey, 'Content-Type': 'application/json' },
    // polyline вдвое компактнее GeoJSON (Москва-Алматы: 27 КБ против 59 КБ в gzip)
    body: JSON.stringify({ ...request, geometryFormat: 'POLYLINE' }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    // 4xx - проблема запроса (нет пути, точка в море), текст понятен
    // пользователю; 5xx - сбой сервиса, подробности ему ни к чему
    throw new RouteError(
      response.status < 500 && body?.error ? body.error : 'Сервис маршрутов сейчас недоступен',
    );
  }

  return parseRoute((await response.json()) as RouteResponse);
}

function parseRoute(response: RouteResponse & { geometry?: LineString | null }): Route {
  const { polyline, tollDistanceKm, tollSections, alternatives, ...route } = response;
  // geometry - ответ бэкенда без поддержки geometryFormat (до перехода на
  // polyline), отсутствие tollSections - до подсветки платных участков;
  // оставлено, чтобы фронт и бэкенд можно было выкладывать в любом порядке
  return {
    ...route,
    geometry: polyline ? decodePolyline(polyline) : route.geometry!,
    tollDistanceKm: tollDistanceKm ?? 0,
    tollSections: (tollSections ?? []).map(section => ({
      distanceKm: section.distanceKm,
      geometry: decodePolyline(section.polyline),
    })),
    alternatives: (alternatives ?? []).map(parseRoute),
  };
}
